import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

/**
 * Chart Controller for TCO visualization
 * Uses Chart.js for bar and line charts
 */
export default class extends Controller {
  static targets = ["canvas", "legend", "downloadButton"]

  static values = {
    type: { type: String, default: "bar" },
    data: Object,
    options: Object,
    title: String,
  }

  connect() {
    if (this.hasCanvasTarget && this.dataValue) {
      this.setupAccessibility()
      this.initChart()
    }
  }

  /**
   * Setup ARIA attributes for accessibility
   */
  setupAccessibility() {
    // Set role and label on canvas
    this.canvasTarget.setAttribute("role", "img")
    this.canvasTarget.setAttribute(
      "aria-label",
      this.titleValue || "Chart visualization",
    )

    // Generate and set screen reader description
    const description = this.generateChartDescription()
    if (description) {
      const descId = `${this.canvasTarget.id || "chart"}-desc`
      this.canvasTarget.setAttribute("aria-describedby", descId)

      // Create hidden description element if not exists
      let descEl = document.getElementById(descId)
      if (!descEl) {
        descEl = document.createElement("div")
        descEl.id = descId
        descEl.className = "sr-only"
        this.canvasTarget.parentNode.insertBefore(descEl, this.canvasTarget)
      }
      descEl.textContent = description
    }
  }

  /**
   * Generate screen reader description from chart data
   */
  generateChartDescription() {
    if (!this.dataValue || !this.dataValue.datasets) return null

    const { labels, datasets } = this.dataValue
    const type = this.typeValue

    if (type === "bar") {
      // Describe bar chart data
      const summaries = datasets.map((ds) => {
        const total = ds.data.reduce((sum, val) => sum + (val || 0), 0)
        return `${ds.label}: ${this.formatCurrency(total)} total`
      })
      return `Bar chart comparing costs. ${summaries.join(". ")}.`
    } else if (type === "line") {
      // Describe line chart trends
      const summaries = datasets.map((ds) => {
        const final = ds.data[ds.data.length - 1] || 0
        return `${ds.label} reaches ${this.formatCurrency(final)} by year ${labels.length}`
      })
      return `Line chart showing cumulative costs over ${labels.length} years. ${summaries.join(". ")}.`
    }

    return `${type} chart with ${datasets.length} data series.`
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  /**
   * Initialize Chart.js chart
   */
  initChart() {
    const ctx = this.canvasTarget.getContext("2d")

    const defaultOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: true,
          position: "top",
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed.y || context.parsed
              return `${context.dataset.label}: ${this.formatCurrency(value)}`
            },
          },
        },
      },
      scales:
        this.typeValue === "line"
          ? {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: (value) => this.formatCurrency(value),
                },
              },
            }
          : {},
    }

    const mergedOptions = { ...defaultOptions, ...this.optionsValue }

    this.chart = new Chart(ctx, {
      type: this.typeValue,
      data: this.dataValue,
      options: mergedOptions,
    })
  }

  /**
   * Update chart with new data
   */
  update(event) {
    if (event && event.detail && event.detail.data) {
      this.dataValue = event.detail.data
    }

    if (this.chart) {
      this.chart.data = this.dataValue
      this.chart.update("active")
    }
  }

  /**
   * Callback when data value changes
   */
  dataValueChanged() {
    if (this.chart && this.dataValue) {
      this.chart.data = this.dataValue
      this.chart.update("active")
    }
  }

  /**
   * Download chart as PNG
   */
  download() {
    if (!this.chart) return

    const link = document.createElement("a")
    link.download = `${this.titleValue || "tco-chart"}.png`
    link.href = this.canvasTarget.toDataURL("image/png")
    link.click()
  }

  /**
   * Handle window resize
   */
  resize() {
    if (this.chart) {
      this.chart.resize()
    }
  }

  /**
   * Format currency value
   */
  formatCurrency(value) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  /**
   * Set chart colors based on TCO comparison
   */
  static get tcoColors() {
    return {
      asafe: {
        background: "rgba(34, 197, 94, 0.8)", // green-500
        border: "rgb(34, 197, 94)",
      },
      steel: {
        background: "rgba(239, 68, 68, 0.8)", // red-500
        border: "rgb(239, 68, 68)",
      },
      boplan: {
        background: "rgba(234, 179, 8, 0.8)", // yellow-500
        border: "rgb(234, 179, 8)",
      },
    }
  }

  /**
   * Generate bar chart data for TCO comparison
   */
  static generateBarData(
    asafe,
    steel,
    boplan,
    labels = ["Product Cost", "Labour", "Maintenance", "Downtime"],
  ) {
    const colors = this.tcoColors

    return {
      labels,
      datasets: [
        {
          label: "A-SAFE",
          data: asafe,
          backgroundColor: colors.asafe.background,
          borderColor: colors.asafe.border,
          borderWidth: 1,
        },
        {
          label: "Steel",
          data: steel,
          backgroundColor: colors.steel.background,
          borderColor: colors.steel.border,
          borderWidth: 1,
        },
        {
          label: "Boplan",
          data: boplan,
          backgroundColor: colors.boplan.background,
          borderColor: colors.boplan.border,
          borderWidth: 1,
        },
      ],
    }
  }

  /**
   * Generate line chart data for cumulative TCO
   */
  static generateLineData(asafe, steel, boplan, years) {
    const colors = this.tcoColors
    const labels = Array.from({ length: years }, (_, i) => `Year ${i + 1}`)

    return {
      labels,
      datasets: [
        {
          label: "A-SAFE",
          data: asafe,
          borderColor: colors.asafe.border,
          backgroundColor: colors.asafe.background,
          fill: false,
          tension: 0.1,
        },
        {
          label: "Steel",
          data: steel,
          borderColor: colors.steel.border,
          backgroundColor: colors.steel.background,
          fill: false,
          tension: 0.1,
        },
        {
          label: "Boplan",
          data: boplan,
          borderColor: colors.boplan.border,
          backgroundColor: colors.boplan.background,
          fill: false,
          tension: 0.1,
        },
      ],
    }
  }
}
